import React from 'react';
import { Award, Users, Clock, CheckCircle2 } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Award,
      title: 'Expert Craftsmanship',
      description: 'Decades of combined experience delivering exceptional renovation results',
    },
    {
      icon: Users,
      title: 'Client-Focused',
      description: 'Your vision drives our work. We listen, adapt, and deliver beyond expectations',
    },
    {
      icon: Clock,
      title: 'On-Time Delivery',
      description: 'We respect your schedule and complete projects within agreed timelines',
    },
    {
      icon: CheckCircle2,
      title: 'Quality Materials',
      description: 'Only premium, durable materials that stand the test of time',
    },
  ];

  return (
    <section id="about" className="py-24 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-1/4 right-0 w-1/3 h-1/2 bg-blue-50/40 rounded-full blur-3xl -z-10" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full border border-blue-100">
              <span className="text-[#0070C0] font-semibold text-sm">ABOUT US</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
              Building Dreams, One Renovation at a Time
            </h2>

            <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
              <p>
                <strong className="text-gray-900">N-Teek Home Renovations Inc.</strong> is a trusted
                name in interior renovations across Canada. We specialize in transforming residential
                and commercial spaces with precision, creativity, and unwavering commitment to quality.
              </p>

              <p>
                From custom kitchens and legal basement conversions to elegant bathroom makeovers and
                modern TV wall installations, we handle every aspect of interior renovation with
                expertise and care. Our team works closely with you from initial consultation through
                final inspection, ensuring your vision becomes reality.
              </p>

              <p>
                What sets us apart is our dedication to both aesthetics and functionality. We don't
                just renovate spaces—we create environments where memories are made, businesses thrive,
                and property values increase. With full licensing, insurance, and compliance with all
                city regulations, you can trust us to deliver exceptional results every time.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-8 pt-8 border-t border-gray-200">
              <div>
                <div className="text-4xl font-bold text-[#0070C0] mb-2">500+</div>
                <div className="text-gray-600 font-medium">Projects Completed</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[#0070C0] mb-2">100%</div>
                <div className="text-gray-600 font-medium">Client Satisfaction</div>
              </div>
            </div>
          </div>

          {/* Right Content - Values Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200 hover:border-[#0070C0] hover:shadow-xl transition-all duration-500 group"
                >
                  <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#0070C0] transition-colors duration-500">
                    <Icon className="w-7 h-7 text-[#0070C0] group-hover:text-white transition-colors duration-500" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-[#0070C0] transition-colors">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="mt-24 bg-gradient-to-r from-[#0070C0] to-[#005A9C] rounded-3xl p-12 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          
          <div className="relative z-10 text-center max-w-4xl mx-auto">
            <h3 className="text-3xl md:text-4xl font-bold mb-6">
              Why Choose N-Teek Home Renovations?
            </h3>
            <p className="text-xl text-blue-50 mb-8 leading-relaxed">
              We're more than contractors—we're partners in bringing your vision to life. With
              comprehensive services, transparent pricing, and a track record of excellence, we make
              renovation stress-free and rewarding.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-semibold">Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-semibold">City Compliant</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-semibold">Premium Materials</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-semibold">Expert Team</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
